import { supabase } from "@/integrations/supabase/client";

// Throttle: don't spam the same error within this window (ms).
const DEDUPE_WINDOW_MS = 60_000;
const recent = new Map<string, number>();

// Substrings that indicate noise we don't want to log
// (browser extensions, ad blockers, third-party scripts the user can't fix).
const IGNORE_PATTERNS = [
  "ResizeObserver loop",
  "Non-Error promise rejection captured",
  "Script error.",
  "ChunkLoadError",
  "Loading chunk",
  "Loading CSS chunk",
  "extension://",
  "moz-extension://",
];

function shouldIgnore(message: string): boolean {
  return IGNORE_PATTERNS.some((p) => message.includes(p));
}

interface ReportInput {
  message: string;
  stack?: string;
  source?: string;
  severity?: "info" | "warning" | "error" | "critical";
  metadata?: Record<string, unknown>;
}

async function report(input: ReportInput) {
  try {
    if (!input.message || shouldIgnore(input.message)) return;

    const key = `${input.source ?? ""}|${input.message.slice(0, 200)}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recent.set(key, now);

    const { data: userData } = await supabase.auth.getUser();

    await supabase.from("client_errors").insert({
      user_id: userData.user?.id ?? null,
      message: input.message.slice(0, 2000),
      stack: input.stack?.slice(0, 8000) ?? null,
      source: input.source ?? null,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      severity: input.severity ?? "error",
      metadata: (input.metadata ?? {}) as never,
    });
  } catch {
    // Never let the reporter itself break the app.
  }
}

let installed = false;

export function installErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    void report({
      message: event.message || String(event.error),
      stack: event.error?.stack,
      source: "window.error",
      severity: "error",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : JSON.stringify(reason)?.slice(0, 500) ?? "Unhandled rejection";
    void report({
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      source: "unhandledrejection",
      severity: "error",
    });
  });
}

export const errorReporter = { report };
